import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";

import { selectIdentity } from "../../../selectors/userSelectors";
import Spinner from "../../../shared/ui/Loading/Spinner";
import EmptyState from "../../../shared/ui/EmptyState/EmptyState";
import { messageFromError } from "../../../shared/api/drfErrors";

import { fetchMyCompany, leaveCompany } from "../api/companyWorkspace";
import { isCompanyFinanceEnabled } from "../../../config/companyFinance";
import { isCompanyOverviewEnabled } from "../../../config/companyOverview";
import {
  canOrdinaryAdminWrite,
  canTransferOwnership,
  companyStatusLabel,
  isActiveMembership,
  isOwnerMembership,
  membershipStatusLabel,
  roleLabel,
} from "../lib/companyLabels";
import { createScopeGate, sameActorId } from "../lib/scopeSession";
import {
  FOCUS_REVALIDATION_UNRESOLVED_MESSAGE,
  openBackgroundRevalidation,
  settleRevalidationWrites,
  useCompanyFocusRevalidation,
} from "../lib/companyFocusRevalidation";
import CompanyPageHeader from "../ui/CompanyPageHeader";

function CompanyMineContent() {
  const navigate = useNavigate();
  const identity = useSelector(selectIdentity);
  const actorId = identity?.userId ?? null;
  const scopeGate = useRef(createScopeGate()).current;
  const mountedRef = useRef(true);
  const busyOpRef = useRef(null);
  const [state, setState] = useState("loading");
  const stateRef = useRef(state);
  const [writesSuspended, setWritesSuspended] = useState(false);
  const writesSuspendedRef = useRef(false);
  const revalidationTokenRef = useRef(0);
  stateRef.current = state;
  writesSuspendedRef.current = writesSuspended;
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null);
  const [authorityActorId, setAuthorityActorId] = useState(null);
  const [leaveBusy, setLeaveBusy] = useState(false);
  const [leaveError, setLeaveError] = useState(null);

  const releaseBusy = (op) => {
    if (busyOpRef.current === op) {
      busyOpRef.current = null;
      setLeaveBusy(false);
    }
  };

  const load = useCallback(({ background = false } = {}) => {
    const keepVisible = background && stateRef.current === "ready";
    const op = scopeGate.beginRead({ actorId });
    const tokenAtStart = revalidationTokenRef.current;
    let backgroundToken = null;
    let revalidationConfirmed = false;
    if (keepVisible) {
      backgroundToken = openBackgroundRevalidation(
        revalidationTokenRef,
        writesSuspendedRef,
        setWritesSuspended,
      );
    } else {
      setState("loading");
      setError(null);
    }
    return fetchMyCompany()
      .then((data) => {
        if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) {
          return;
        }
        setPayload(data);
        setAuthorityActorId(actorId);
        revalidationConfirmed = true;
        setLeaveError((current) =>
          current === FOCUS_REVALIDATION_UNRESOLVED_MESSAGE ? null : current,
        );
        setState("ready");
      })
      .catch((err) => {
        if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) {
          return;
        }
        const status = err?.response?.status;
        if (keepVisible && status !== 403 && status !== 404) {
          setLeaveError(FOCUS_REVALIDATION_UNRESOLVED_MESSAGE);
          return;
        }
        setPayload(null);
        setAuthorityActorId(null);
        setError(messageFromError(err));
        setState("error");
      })
      .finally(() => {
        settleRevalidationWrites({
          keepVisible: keepVisible,
          confirmed: revalidationConfirmed,
          token: backgroundToken,
          tokenAtStart,
          tokenRef: revalidationTokenRef,
          writesSuspendedRef,
          setWritesSuspended,
        });
      });
  }, [actorId, scopeGate]);

  useEffect(() => {
    mountedRef.current = true;
    scopeGate.bindIdentity({ actorId, companyId: null });
    busyOpRef.current = null;
    setLeaveBusy(false);
    setLeaveError(null);
    setPayload(null);
    setAuthorityActorId(null);
    load();
    return () => {
      mountedRef.current = false;
      scopeGate.invalidate();
      busyOpRef.current = null;
    };
  }, [actorId]); // eslint-disable-line react-hooks/exhaustive-deps

  useCompanyFocusRevalidation({
    enabled: state === "ready",
    busy: leaveBusy,
    onRevalidate: () => load({ background: true }),
  });

  const actorMatches =
    authorityActorId != null && sameActorId(authorityActorId, actorId);
  const company = actorMatches ? payload?.company || null : null;
  const membership = actorMatches ? payload?.membership || null : null;

  const handleLeave = async () => {
    if (!company || busyOpRef.current || writesSuspendedRef.current) return;
    const confirmed = window.confirm(
      `Покинути компанію «${company.name}»? Корпоративний доступ буде втрачено. Особисті клієнти, об'єкти та фінанси залишаться вашими.`,
    );
    if (!confirmed) return;
    const op = scopeGate.beginMutation({
      actorId,
      companyId: company.id,
    });
    busyOpRef.current = op;
    setLeaveBusy(true);
    setLeaveError(null);
    try {
      await leaveCompany(company.id);
      if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      setPayload({ company: null, membership: null });
      setAuthorityActorId(actorId);
    } catch (err) {
      if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      setLeaveError(messageFromError(err));
    } finally {
      releaseBusy(op);
    }
  };

  if (state === "loading" || (state === "ready" && !actorMatches)) {
    return (
      <div className="feature-company-workspace__state">
        <Spinner size="lg" label="Завантаження компанії" />
      </div>
    );
  }

  if (state === "error") {
    return (
      <div className="feature-company-workspace__state">
        <EmptyState
          icon="fa-solid fa-triangle-exclamation"
          title="Не вдалося завантажити"
          description={error || "Спробуйте оновити сторінку."}
          size="md"
        />
        <button type="button" className="action-btn" onClick={load}>
          Повторити
        </button>
      </div>
    );
  }

  if (!company || !membership) {
    return (
      <section className="clients-page feature-company-workspace" id="Csection" tabIndex="-1">
        <div className="container">
          <CompanyPageHeader
            title="Моя компанія"
            subtitle="У вас зараз немає активного або призупиненого членства."
            actions={
              <>
                <Link to="/companies/new" className="action-btn">
                  Створити компанію
                </Link>
                <Link to="/companies/find" className="action-btn action-btn--ghost">
                  Знайти компанію
                </Link>
              </>
            }
          />
          <div className="feature-company-workspace__actions-grid">
            <Link to="/companies/join-request" className="feature-company-workspace__card-link">
              Подати заявку на вступ
            </Link>
            <Link to="/companies/my-invitations" className="feature-company-workspace__card-link">
              Мої запрошення
            </Link>
            <Link to="/companies/my-join-requests" className="feature-company-workspace__card-link">
              Мої заявки
            </Link>
          </div>
        </div>
      </section>
    );
  }

  if (membership.status === "suspended") {
    return (
      <section className="clients-page feature-company-workspace" id="Csection" tabIndex="-1">
        <div className="container">
          <CompanyPageHeader
            title="Моя компанія"
            subtitle="Ваше членство призупинено. Приватний вміст компанії недоступний."
            company={company}
            membership={membership}
          />
          <article className="feature-company-workspace__card">
            <h2>{company.name}</h2>
            <p>
              Статус членства: {membershipStatusLabel(membership.status)}. Роль і
              керівні дії закриті, доки адміністратор не відновить доступ.
            </p>
            {leaveError ? (
              <p className="feature-company-workspace__error" role="alert">
                {leaveError}
              </p>
            ) : null}
            <button
              type="button"
              className="action-btn action-btn--danger"
              onClick={handleLeave}
              disabled={leaveBusy}
            >
              {leaveBusy ? "Вихід…" : "Покинути компанію"}
            </button>
            {isCompanyFinanceEnabled() ? (
              <Link
                to={`/companies/${company.id}/my-remuneration`}
                className="action-btn"
                data-testid="company-mine-remuneration-cta"
              >
                Моя винагорода
              </Link>
            ) : null}
          </article>
        </div>
      </section>
    );
  }

  const admin = canOrdinaryAdminWrite(company, membership);
  const archived = company.status === "archived";
  const owner = isOwnerMembership(company, membership);
  const canTransfer = canTransferOwnership(company, membership);
  const showOverviewCta =
    isCompanyOverviewEnabled() && isActiveMembership(company, membership);

  return (
    <section className="clients-page feature-company-workspace" id="Csection" tabIndex="-1">
      <div className="container">
        <CompanyPageHeader
          title="Моя компанія"
          subtitle={
            archived
              ? "Компанія в архіві. Доступні лише підтримувані дії."
              : "Робоча оболонка компанії: профіль, команда та вступ."
          }
          company={company}
          membership={membership}
          companyId={company.id}
          activeNav="profile"
          actions={
            <button
              type="button"
              className="action-btn"
              onClick={() => navigate(`/companies/${company.id}`)}
            >
              Відкрити профіль
            </button>
          }
        />

        {archived && owner ? (
          <div className="feature-company-workspace__banner" role="status">
            Архівна компанія: звичайні зміни закриті. Передача власності, вихід і
            аварійні дії команди залишаються доступними за правилами сервера.
          </div>
        ) : null}

        <article className="feature-company-workspace__card">
          <h2>{company.name}</h2>
          <dl className="feature-company-workspace__facts">
            <div>
              <dt>Роль</dt>
              <dd>{roleLabel(membership.role)}</dd>
            </div>
            <div>
              <dt>Статус компанії</dt>
              <dd>{companyStatusLabel(company.status)}</dd>
            </div>
            <div>
              <dt>Місто</dt>
              <dd>{company.city || "—"}</dd>
            </div>
          </dl>
          <div className="feature-company-workspace__inline-actions">
            {showOverviewCta ? (
              <Link
                to={`/companies/${company.id}/overview`}
                className="action-btn"
                data-testid="company-mine-overview-cta"
              >
                Огляд компанії
              </Link>
            ) : null}
            <Link to={`/companies/${company.id}`} className="action-btn">
              Профіль
            </Link>
            <Link to={`/companies/${company.id}/members`} className="action-btn action-btn--ghost">
              Команда
            </Link>
            {canTransfer ? (
              <Link
                to={`/companies/${company.id}/members`}
                className="action-btn action-btn--ghost"
              >
                Передати власність
              </Link>
            ) : null}
            {admin ? (
              <Link
                to={`/companies/${company.id}/settings`}
                className="action-btn action-btn--ghost"
              >
                Налаштування
              </Link>
            ) : null}
            {admin ? (
              <Link
                to={`/companies/${company.id}/invitations`}
                className="action-btn action-btn--ghost"
              >
                Запрошення
              </Link>
            ) : null}
            {admin ? (
              <Link
                to={`/companies/${company.id}/join-requests`}
                className="action-btn action-btn--ghost"
              >
                Заявки
              </Link>
            ) : null}
          </div>
        </article>
      </div>
    </section>
  );
}

export default CompanyMineContent;
